<template>
  <form autocomplete="off">
    <collapsible-note id="mfa_no" class="note" v-if="noteTitleLabel.length || instructions.length">
      <template #title>
        {{ noteTitleLabel || 'Instructions' }}
      </template>
      <template #content>
        <div v-for="instruction in instructions" :key="instruction.text">
          {{ instruction.text }}
        </div>
      </template>
    </collapsible-note>

    <input-text
      v-for="textInput in textInputs"
      :value="userFieldValues[textInput.fieldID]"
      @input="setUserInputField($event, textInput.fieldID)"
      :type="textInput.type"
      :id="textInput.fieldID"
      :key="textInput.fieldID"
      :placeholder="textInput.text"
      :autocomplete="textInput.autocomplete"
      v-validation="getValidations(textInput)"
    >
      <template #label>
        {{ textInput.name || inputLabel(textInput.fieldID) }}
      </template>
      <template #description v-if="Object.keys(imageField).length">
        <svg id="svg-1" v-bind="{ width: imageField.width, height: imageField.height }">
          <image id="img-1" class="svg-image" v-bind="{ 'xlink:href': imageField.src }" />
        </svg>
      </template>
    </input-text>

    <input-text
      v-for="(inputElement, index) in inputSubElements"
      :value="userFieldValues[inputElement.fieldID]"
      @input="setUserInputField($event, inputElement.fieldID)"
      :type="inputElement.type"
      :id="`${inputElement.type}-${index}-id`"
      :key="`${inputElement.type}-${index}`"
      :placeholder="inputElement.text"
      :disabled="inputElement.htmlAttrs.disabled"
      :autocomplete="inputElement.htmlAttrs.autocomplete"
      :maxlength="inputElement.htmlAttrs.maxlength"
      v-validation="['required']"
    >
      <template #label>
        <p v-if="index === 0">{{ subElementsLabel }}</p>
      </template>
    </input-text>

    <template>
      <div v-for="html in htmlFields" :key="html.fieldID" v-html="html.text"></div>
    </template>

    <input-select
      v-if="Object.keys(selectOption).length"
      :id="selectOption.fieldID"
      :key="selectOption.fieldID"
      :value="userFieldValues[selectOption.fieldID]"
      name="profile-input-select"
      placeholder="Select an Option"
      @change="setUserInputField($event, selectOption.fieldID)"
      :options="getProfileOptions(selectOption.values)"
      v-validation:closed="['required']"
    >
      <template #label>
        {{ inputLabel(selectOption.fieldID) }}
      </template>
    </input-select>

    <brand-button
      v-if="Object.keys(buttonAttributes).length"
      type="button"
      :class="buttonAttributes.class"
      :id="buttonAttributes.id"
      @click="onClick(buttonAttributes)"
    >
      <span id="buttonLabel">{{ buttonAttributes.value }}</span>
    </brand-button>
    <p v-if="buttonAttributes.id === 'mfaButton'">
      You need to verify before proceeding!
    </p>

    <slot name="error-banner" />
  </form>
</template>
<script>
export default {
  name: 'mfa-request-form',
  props: {
    userFieldValues: {
      type: Object,
      default: () => {},
    },
    fields: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    if (this.fields?.length) {
      this.addHiddenFields();
    }
  },
  computed: {
    noteTitleLabel() {
      const title = this.fields.find(field => field.type === 'instructions') || {};

      return title.text || '';
    },
    instructions() {
      const subSet = this.fields.find(field => field.type === 'set') || {};

      return subSet.subElements || [];
    },
    inputSubElements() {
      const inputElements = this.instructions?.filter(subElement => {
        const type = subElement.type.toLowerCase();

        return (type === 'password' || type === 'input') && !subElement?.optional;
      });

      return inputElements || [];
    },
    textInputs() {
      return this.fields.filter(field => {
        const type = field.type.toLowerCase();

        return type === 'text' || type === 'password' || type === 'input';
      });
    },
    selectOption() {
      return this.fields.find(field => field.type.toLowerCase() === 'options') ?? {};
    },
    htmlFields() {
      return this.fields.filter(field => field.type.toLowerCase() === 'html');
    },
    buttonAttributes() {
      const buttonField = this.fields.find(field => field.type.toLowerCase() === 'button');
      return buttonField?.htmlAttrs || {};
    },
    imageField() {
      return this.fields.find(field => field.type.toLowerCase() === 'image') ?? {};
    },
    subElementsLabel() {
      const index = this.fields.findIndex(field => field.type === 'set');
      if (index > -1) {
        return this.inputLabel('', index);
      }
      return '';
    },
  },
  methods: {
    inputLabel(fieldId, fieldIndex) {
      const index = fieldIndex ?? this.fields.findIndex(field => field.fieldID === fieldId);
      const input =
        this.fields
          .slice(0, index)
          .reverse()
          .find(field => field.type.toLowerCase() === 'header') || {};

      return input.text || '';
    },
    getValidations(inputField) {
      const { fieldID, text } = inputField;
      const textToMatch = text?.match(/\((.*)\)/)?.pop();

      if (inputField.optional) {
        return '';
      }

      return fieldID === 'captcha_value'
        ? ['required', ['equalImageText', textToMatch]]
        : ['required'];
    },
    addHiddenFields() {
      const hiddenFields = this.fields.reduce((acc, field) => {
        if (field.type === 'hidden') {
          acc[field.fieldID] = field.value ?? field.htmlAttrs?.value;
        }
        return acc;
      }, {});
      const updatedUserFieldValues = { ...this.userFieldValues, ...hiddenFields };

      this.$emit(
        'userFieldsInput',
        updatedUserFieldValues,
        Object.keys(this.buttonAttributes).length,
      );
    },
    setUserInputField(event, fieldID) {
      const updatedUserFieldValues = { ...this.userFieldValues };
      updatedUserFieldValues[fieldID] = event;

      this.$emit('userFieldsInput', updatedUserFieldValues);
    },
    getProfileOptions(values) {
      const options = Object.keys(values).map(option => {
        return { value: `${option}: ${values[option]}`, key: option };
      });
      return options ?? [];
    },
    onClick(attributes) {
      if (attributes.id === 'mfaButton') {
        const mfaFields = { button_clicked: 1, value: 'Verified' };

        this.$emit('userFieldsInput', { ...this.userFieldValues, ...mfaFields }, false);
      }
    },
  },
};
</script>
<style lang="sass" scoped>
.note
  margin-top: baseunit(4)
</style>
